/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { JSX } from 'react';

// Keyframes require @compiled/react rather than @atlaskit/css.
import { cssMap, jsx, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';

// 200ms ease-in, 125ms linear, 200ms ease-out, 200ms ease-in.
const rotate = keyframes({
	'0%': { transform: 'rotate(90deg)', animationTimingFunction: 'ease-in' },
	'27.5862%': { transform: 'rotate(180deg)', animationTimingFunction: 'linear' },
	'44.8276%': { transform: 'rotate(270deg)', animationTimingFunction: 'ease-out' },
	'72.4138%': { transform: 'rotate(360deg)', animationTimingFunction: 'ease-in' },
	'100%': { transform: 'rotate(450deg)' },
});

const colour = keyframes({
	'0%': { stroke: token('color.rovo.border.lime'), animationTimingFunction: 'ease-in' },
	'27.5862%': { stroke: token('color.rovo.border.blue'), animationTimingFunction: 'linear' },
	'44.8276%': { stroke: token('color.rovo.border.purple'), animationTimingFunction: 'ease-out' },
	'72.4138%': { stroke: token('color.rovo.border.saffron'), animationTimingFunction: 'ease-in' },
	'100%': { stroke: token('color.rovo.border.lime') },
});

// The first three transitions reveal the arc; subsequent rotations keep its full length.
const reveal = keyframes({
	from: { strokeDasharray: '1 99' },
	to: { strokeDasharray: '25 75' },
});

const styles = cssMap({
	spinner: {
		display: 'inline-block',
		verticalAlign: 'middle',
		flexShrink: 0,
		transform: 'rotate(90deg)',
		transformOrigin: 'center',
		animation: `${rotate} 725ms linear infinite`,
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	arc: {
		fill: 'none',
		stroke: token('color.rovo.border.lime'),
		animation: `${colour} 725ms linear infinite, ${reveal} 525ms linear forwards`,
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
		'@media (forced-colors: active)': {
			animation: 'none',
			stroke: 'CanvasText',
		},
	},
});

export const QuickFindLoadingSpinner = (): JSX.Element => (
	<svg
		css={styles.spinner}
		width={24}
		height={24}
		viewBox="0 0 24 24"
		aria-hidden="true"
		focusable="false"
		data-testid="quick-find-loading-spinner"
	>
		<circle
			css={styles.arc}
			cx={12}
			cy={12}
			r={10}
			pathLength={100}
			strokeWidth={2}
			strokeLinecap="butt"
			strokeDasharray="25 75"
			transform="rotate(-180 12 12)"
		/>
	</svg>
);
